'use client';

import Image from 'next/image';
import { useState, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { z } from 'zod';

import { Button } from '../button.js';
import { Input } from '../input.js';
import { Label } from '../label.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../card.js';
import { Alert, AlertDescription } from '../alert.js';
import { Loader2 } from 'lucide-react';
import Link from 'next/link';
import { GoogleSignInButton } from '../oauth/GoogleSignInButton.js';
import type { AuthConfig } from '../../lib/auth/auth-config.js';

// Validation schemas
const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

const signUpSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
});

type LoginData = z.infer<typeof loginSchema>;
type SignUpData = z.infer<typeof signUpSchema>;

interface SharedAuthPageProps {
  /**
   * Authentication configuration
   */
  config: AuthConfig;

  /**
   * Function to handle email/password sign in
   */
  onSignIn: (email: string, password: string) => Promise<{ error?: string }>;

  /**
   * Function to handle email/password sign up
   */
  onSignUp?: (email: string, password: string, firstName: string, lastName: string) => Promise<{ error?: string }>;

  /**
   * Function to handle Google sign in
   */
  onGoogleSignIn: (redirectPath?: string) => Promise<{ error?: string }>;

  /**
   * Function to handle magic link
   */
  onMagicLink?: (email: string) => Promise<{ error?: string }>;

  /**
   * Optional custom className
   */
  className?: string;
}

/**
 * Inner component that uses useSearchParams (must be inside Suspense)
 */
function SharedAuthPageContent({
  config,
  onSignIn,
  onSignUp,
  onGoogleSignIn,
  onMagicLink,
  className,
}: SharedAuthPageProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSignUp, setIsSignUp] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
  });
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams?.get('callbackUrl') || config.redirects?.onLoginSuccess || '/dashboard';

  const validateLoginForm = () => {
    try {
      loginSchema.parse({ email: formData.email, password: formData.password });
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        setError(err.issues[0].message);
      }
      return false;
    }
  };

  const validateSignUpForm = () => {
    try {
      signUpSchema.parse(formData);
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        setError(err.issues[0].message);
      }
      return false;
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateLoginForm()) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await onSignIn(formData.email, formData.password);

      if (result.error) {
        setError(result.error);
      } else {
        config.callbacks?.onLoginSuccess?.(formData);
        router.push(callbackUrl);
        router.refresh();
      }
    } catch (err) {
      console.error('Sign in error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!onSignUp) {
      setError('Sign up is not available');
      return;
    }

    if (!validateSignUpForm()) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await onSignUp(formData.email, formData.password, formData.firstName, formData.lastName);

      if (result.error) {
        setError(result.error);
      } else {
        config.callbacks?.onSignUpSuccess?.(formData);
        setSuccess('Account created successfully! Please check your email to verify your account.');
        setTimeout(() => {
          setIsSignUp(false);
          setFormData({ email: '', password: '', firstName: '', lastName: '' });
        }, 2000);
      }
    } catch (err) {
      console.error('Sign up error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await onGoogleSignIn(callbackUrl);

      if (result.error) {
        setError(result.error);
        setIsLoading(false);
      }
      // If successful, the redirect will happen automatically
    } catch (err) {
      console.error('Google sign-in error:', err);
      setError('An unexpected error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  const handleMagicLink = async () => {
    if (!onMagicLink) {
      setError('Magic link is not available');
      return;
    }

    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await onMagicLink(formData.email);

      if (result.error) {
        setError(result.error);
      } else {
        setSuccess('Check your email for the magic link!');
      }
    } catch (err) {
      console.error('Magic link error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const themeClass = config.theme?.customClass || '';
  const containerClass = className || 'min-h-screen flex items-center justify-center bg-card-50 py-12 px-4 sm:px-6 lg:px-8';

  return (
    <div className={`${containerClass} ${themeClass}`}>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          {config.logoUrl && (
            <div className="flex justify-center mb-4">
              <Image
                src={config.logoUrl}
                alt={config.logoAlt || config.appName}
                width={200}
                height={48}
                className="h-12 w-auto"
              />
            </div>
          )}
          <CardTitle className="text-2xl font-bold text-center">
            {isSignUp ? config.signUpTitle : config.loginTitle}
          </CardTitle>
          <CardDescription className="text-center">
            {isSignUp ? config.signUpDescription : config.loginDescription}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={isSignUp ? handleSignUpSubmit : handleSignIn}
            className="space-y-4"
          >
            {isSignUp && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="Enter your first name"
                    disabled={isLoading}
                    className="w-full"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Enter your last name"
                    disabled={isLoading}
                    className="w-full"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                disabled={isLoading}
                className="w-full"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder={isSignUp ? 'Create a password' : 'Enter your password'}
                disabled={isLoading}
                className="w-full"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert>
                <AlertDescription>{success}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isSignUp ? 'Sign Up' : 'Sign In'}
            </Button>
          </form>

          {!isSignUp && (
            <div className="text-center mt-2">
              {config.forgotPasswordUrl && (
                <Link href={config.forgotPasswordUrl} className="text-sm text-primary hover:underline">
                  Forgot your password?
                </Link>
              )}
            </div>
          )}

          {!isSignUp && (config.authMethods?.google || config.authMethods?.magicLink) && (
            <div className="mt-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                </div>
              </div>

              {config.authMethods?.google && (
                <GoogleSignInButton
                  onClick={handleGoogleSignIn}
                  isLoading={isLoading}
                  className="mt-4"
                />
              )}

              {config.authMethods?.magicLink && config.showMagicLink && (
                <Button
                  variant="outline"
                  className="w-full mt-2"
                  onClick={handleMagicLink}
                  disabled={isLoading}
                  type="button"
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Send Magic Link
                </Button>
              )}
            </div>
          )}

          <div className="mt-4 text-center text-sm">
            <p className="text-muted-foreground">
              {isSignUp ? (
                <>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setIsSignUp(false)}
                    className="text-primary hover:underline"
                  >
                    Sign in
                  </button>
                </>
              ) : (
                <>
                  {config.showSignUp && (
                    <>
                      Don&apos;t have an account?{' '}
                      <button
                        type="button"
                        onClick={() => setIsSignUp(true)}
                        className="text-primary hover:underline"
                      >
                        Sign up
                      </button>
                    </>
                  )}
                </>
              )}
            </p>
            <p className="text-muted-foreground mt-2">{config.helpText}</p>
            {config.supportEmail && (
              <p className="text-muted-foreground text-xs mt-1">
                Email: <a href={`mailto:${config.supportEmail}`} className="text-primary hover:underline">{config.supportEmail}</a>
              </p>
            )}
          </div>

          {config.footerContent && <div className="mt-4">{config.footerContent}</div>}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Fallback component for Suspense (no-op URL sync)
 */
function SharedAuthPageFallback({
  config,
  onSignIn,
  onSignUp,
  onGoogleSignIn,
  onMagicLink,
  className,
}: SharedAuthPageProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSignUp, setIsSignUp] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
  });
  const router = useRouter();
  // Fallback: use default redirect (no URL sync during static generation)
  const callbackUrl = config.redirects?.onLoginSuccess || '/dashboard';

  const validateLoginForm = () => {
    try {
      loginSchema.parse({ email: formData.email, password: formData.password });
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        setError(err.issues[0].message);
      }
      return false;
    }
  };

  const validateSignUpForm = () => {
    try {
      signUpSchema.parse(formData);
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        setError(err.issues[0].message);
      }
      return false;
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateLoginForm()) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await onSignIn(formData.email, formData.password);

      if (result.error) {
        setError(result.error);
      } else {
        config.callbacks?.onLoginSuccess?.(formData);
        router.push(callbackUrl);
        router.refresh();
      }
    } catch (err) {
      console.error('Sign in error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!onSignUp) {
      setError('Sign up is not available');
      return;
    }

    if (!validateSignUpForm()) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await onSignUp(formData.email, formData.password, formData.firstName, formData.lastName);

      if (result.error) {
        setError(result.error);
      } else {
        config.callbacks?.onSignUpSuccess?.(formData);
        setSuccess('Account created successfully! Please check your email to verify your account.');
        setTimeout(() => {
          setIsSignUp(false);
          setFormData({ email: '', password: '', firstName: '', lastName: '' });
        }, 2000);
      }
    } catch (err) {
      console.error('Sign up error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await onGoogleSignIn(callbackUrl);

      if (result.error) {
        setError(result.error);
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Google sign-in error:', err);
      setError('An unexpected error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  const handleMagicLink = async () => {
    if (!onMagicLink) {
      setError('Magic link is not available');
      return;
    }

    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await onMagicLink(formData.email);

      if (result.error) {
        setError(result.error);
      } else {
        setSuccess('Check your email for the magic link!');
      }
    } catch (err) {
      console.error('Magic link error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const themeClass = config.theme?.customClass || '';
  const containerClass = className || 'min-h-screen flex items-center justify-center bg-card-50 py-12 px-4 sm:px-6 lg:px-8';

  return (
    <div className={`${containerClass} ${themeClass}`}>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          {config.logoUrl && (
            <div className="flex justify-center mb-4">
              <Image
                src={config.logoUrl}
                alt={config.logoAlt || config.appName}
                width={200}
                height={48}
                className="h-12 w-auto"
              />
            </div>
          )}
          <CardTitle className="text-2xl font-bold text-center">
            {isSignUp ? config.signUpTitle : config.loginTitle}
          </CardTitle>
          <CardDescription className="text-center">
            {isSignUp ? config.signUpDescription : config.loginDescription}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={isSignUp ? handleSignUpSubmit : handleSignIn}
            className="space-y-4"
          >
            {isSignUp && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="Enter your first name"
                    disabled={isLoading}
                    className="w-full"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Enter your last name"
                    disabled={isLoading}
                    className="w-full"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                disabled={isLoading}
                className="w-full"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder={isSignUp ? 'Create a password' : 'Enter your password'}
                disabled={isLoading}
                className="w-full"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert>
                <AlertDescription>{success}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isSignUp ? 'Sign Up' : 'Sign In'}
            </Button>
          </form>

          {!isSignUp && (
            <div className="text-center mt-2">
              {config.forgotPasswordUrl && (
                <Link href={config.forgotPasswordUrl} className="text-sm text-primary hover:underline">
                  Forgot your password?
                </Link>
              )}
            </div>
          )}

          {!isSignUp && (config.authMethods?.google || config.authMethods?.magicLink) && (
            <div className="mt-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                </div>
              </div>

              {config.authMethods?.google && (
                <GoogleSignInButton
                  onClick={handleGoogleSignIn}
                  isLoading={isLoading}
                  className="mt-4"
                />
              )}

              {config.authMethods?.magicLink && config.showMagicLink && (
                <Button
                  variant="outline"
                  className="w-full mt-2"
                  onClick={handleMagicLink}
                  disabled={isLoading}
                  type="button"
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Send Magic Link
                </Button>
              )}
            </div>
          )}

          <div className="mt-4 text-center text-sm">
            <p className="text-muted-foreground">
              {isSignUp ? (
                <>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setIsSignUp(false)}
                    className="text-primary hover:underline"
                  >
                    Sign in
                  </button>
                </>
              ) : (
                <>
                  {config.showSignUp && (
                    <>
                      Don&apos;t have an account?{' '}
                      <button
                        type="button"
                        onClick={() => setIsSignUp(true)}
                        className="text-primary hover:underline"
                      >
                        Sign up
                      </button>
                    </>
                  )}
                </>
              )}
            </p>
            <p className="text-muted-foreground mt-2">{config.helpText}</p>
            {config.supportEmail && (
              <p className="text-muted-foreground text-xs mt-1">
                Email: <a href={`mailto:${config.supportEmail}`} className="text-primary hover:underline">{config.supportEmail}</a>
              </p>
            )}
          </div>

          {config.footerContent && <div className="mt-4">{config.footerContent}</div>}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Main export component with Suspense boundary
 */
export function SharedAuthPage(props: SharedAuthPageProps) {
  return (
    <Suspense fallback={<SharedAuthPageFallback {...props} />}>
      <SharedAuthPageContent {...props} />
    </Suspense>
  );
}
